// trendDeltas.ts
import type { ConnectorStats, ExecutionTrendData } from '@/hooks/useConnectorStats';

export interface TrendDelta {
  value: number;
  direction: 'up' | 'down' | 'neutral';
  label: string;
}
export interface StatsTrends {
  executions?: TrendDelta;
  successRate?: TrendDelta;
}

const LABEL = 'vs earlier in period';
const PREV_LABEL = 'vs previous period';

function toDelta(signed: number, label: string): TrendDelta {
  const value = Math.abs(Math.round(signed * 10) / 10);
  const direction = signed > 0 ? 'up' : signed < 0 ? 'down' : 'neutral';
  return { value, direction, label };
}

export function computeStatsTrends(trends: ExecutionTrendData[]): StatsTrends {
  if (!trends || trends.length < 2) return {};
  const mid = Math.floor(trends.length / 2);
  const earlier = trends.slice(0, mid);
  const later = trends.slice(mid);

  const sum = (xs: ExecutionTrendData[], k: 'successful' | 'failed' | 'total') =>
    xs.reduce((acc, d) => acc + (d[k] || 0), 0);

  const earlierTotal = sum(earlier, 'total');
  const laterTotal = sum(later, 'total');

  const out: StatsTrends = {};

  // Execution volume: percent change later vs earlier.
  if (earlierTotal > 0) {
    out.executions = toDelta(((laterTotal - earlierTotal) / earlierTotal) * 100, LABEL);
  } else if (laterTotal > 0) {
    out.executions = toDelta(100, LABEL);
  }

  // Success rate: percentage-point change. Needs executions in BOTH halves.
  if (earlierTotal > 0 && laterTotal > 0) {
    const earlierRate = (sum(earlier, 'successful') / earlierTotal) * 100;
    const laterRate = (sum(later, 'successful') / laterTotal) * 100;
    out.successRate = toDelta(laterRate - earlierRate, LABEL);
  }

  return out;
}

export function computeStatsTrendsFromSummaries(
  current: ConnectorStats,
  previous: ConnectorStats,
  label: string = PREV_LABEL,
): StatsTrends {
  const out: StatsTrends = {};

  if (previous.totalExecutions > 0) {
    out.executions = toDelta(
      ((current.totalExecutions - previous.totalExecutions) / previous.totalExecutions) * 100,
      label,
    );
  } else if (current.totalExecutions > 0) {
    out.executions = toDelta(100, label);
  }

  if (previous.totalExecutions > 0 && current.totalExecutions > 0) {
    out.successRate = toDelta(current.successRate - previous.successRate, label);
  }

  return out;
}
